import ReportBrowserErrors from '@/graphql/mutations/ReportBrowserErrors'
import cognitoAuth from '@/cognito'

let appVersion = 'unknown'
let lastKnownToken = null
const queue = []
const recentErrors = new Map()
let flushTimerId = null
const FLUSH_INTERVAL_MS = 5000
const MAX_BATCH_SIZE = 20
const DEDUP_WINDOW_MS = 60000

const getErrorSignature = (message, stack) => {
  const firstLine = (stack || '').split('\n')[0] || ''

  return `${message}::${firstLine}`
}

const isDuplicate = (signature) => {
  const now = Date.now()
  const lastSeen = recentErrors.get(signature)

  if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) {
    return true
  }

  recentErrors.set(signature, now)

  if (recentErrors.size > 100) {
    for (const [key, timestamp] of recentErrors) {
      if (now - timestamp > DEDUP_WINDOW_MS) {
        recentErrors.delete(key)
      }
    }
  }

  return false
}

const scheduleFlush = () => {
  if (flushTimerId) {
    return
  }

  flushTimerId = setTimeout(() => {
    flushTimerId = null
    flush()
  }, FLUSH_INTERVAL_MS)
}

const buildRequestBody = (batch) => JSON.stringify({
  operationName: 'reportBrowserErrors',
  query: ReportBrowserErrors,
  variables: { errors: JSON.stringify(batch) }
})

const sendErrors = async (batch) => {
  const endpoint = process.env.VUE_APP_APPSYNC_GRAPHQL_ENDPOINT
  if (!endpoint) {
    return
  }

  let jwtToken
  try {
    jwtToken = await cognitoAuth.getIdTokenPromise()
  } catch (e) {
    // Auth unavailable — fall back to last known token
  }

  const authToken = jwtToken || lastKnownToken
  if (!authToken) {
    return
  }

  if (jwtToken) {
    lastKnownToken = jwtToken
  }

  await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authToken
    },
    body: buildRequestBody(batch),
    keepalive: true
  })
}

const flush = () => {
  if (queue.length === 0) {
    return
  }

  const batch = queue.splice(0, MAX_BATCH_SIZE)

  try {
    sendErrors(batch).catch(() => {})
  } catch (e) {
    // Silently drop
  }

  if (queue.length > 0) {
    scheduleFlush()
  }
}

const init = ({ version }) => {
  appVersion = version || 'unknown'

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (queue.length === 0 || !lastKnownToken) {
        return
      }

      const endpoint = process.env.VUE_APP_APPSYNC_GRAPHQL_ENDPOINT
      if (!endpoint) {
        return
      }

      const batch = queue.splice(0, MAX_BATCH_SIZE)

      try {
        fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: lastKnownToken
          },
          body: buildRequestBody(batch),
          keepalive: true
        }).catch(() => {})
      } catch (e) {
        // Silently drop
      }
    })
  }
}

const reportError = ({ message, stack, type, extra }) => {
  try {
    const signature = getErrorSignature(message, stack)
    if (isDuplicate(signature)) {
      return
    }

    let safeExtra = {}
    try {
      safeExtra = JSON.parse(JSON.stringify(extra || {}))
    } catch (e) {
      safeExtra = { serializationError: 'extra contained non-serializable data' }
    }

    queue.push({
      message: message || 'Unknown error',
      stack: stack || '',
      type: type || 'unknown',
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      timestamp: new Date().toISOString(),
      appVersion,
      extra: safeExtra
    })

    scheduleFlush()
  } catch (e) {
    // Never throw from the error reporter
  }
}

export default { init, reportError, flush }
